import { useState } from 'react'
import { Head, Link, useForm } from '@inertiajs/react'
import { Card } from '../../components/core/Card.jsx'
import { Button } from '../../components/core/Button.jsx'
import { Divider } from '../../components/core/Divider.jsx'
import { Field } from '../../components/forms/Field.jsx'
import { Input } from '../../components/forms/Input.jsx'
import { Alert } from '../../components/feedback/Alert.jsx'
import { withSignedInLayout } from '../../signed_in/SignedInLayout.jsx'
import { quoted } from '~/marketing/marketingData'

// Six sections, one per backend that exists: the person's name, the email
// address, change password, the businesses on the account, plan and usage,
// delete account.
//
// **Three of these were re-applied on integration** and are in
// `docs/design/app-local-patches.md`. The export drew three sections: the email
// address section was missing entirely though it has been built and tested since
// 2026-09-24, plan and usage was absent because the export predates /billing, and
// deletion was confirmed by typing the email address where the controller
// authenticates a password.

export default function AccountEdit({
  name = '',
  email = '',
  email_verified = true,
  pending_email = null,
  brand_count = 0,
  businesses = [],
  errors = {},
}) {
  // `name ?? ''`, and the parameter default above is not enough. A JavaScript
  // default parameter fires for `undefined` and not for `null`, and `users.name`
  // is nullable — signup asks for email and password only — so Rails sends JSON
  // null for anybody who has never set one, `useForm` holds null, and React logs
  // "`value` prop on `input` should not be null" and treats the field as
  // uncontrolled. Fourth instance of this family in this codebase.
  const profile = useForm({ name: name ?? '' })
  // Pre-filled with the pending address when one is outstanding, which is what
  // makes "send it again" possible without a fourth route: re-submitting the same
  // address mints an equivalent link and sends it. The password is always typed
  // fresh — AccountEmailsController authenticates it on every request.
  const address = useForm({ email: pending_email || '', password: '' })
  // Cancelling takes no password: it can only reduce what an outstanding link is
  // able to do, and the safe direction should not be the harder one.
  const cancel = useForm({})
  const password = useForm({
    current_password: '',
    password: '',
    password_confirmation: '',
  })
  // The password, not a typed address. Removing a brand asks for the name
  // because the risk there is a misclick; the risk here is a session left open
  // on a borrowed laptop, and this is the most destructive action in the
  // product — so the proof has to be something only the owner knows rather than
  // something the screen is already showing them. AccountsController#destroy
  // authenticates params[:password]; nothing is compared here, which is why no
  // typed-match check gates the button.
  const removal = useForm({ password: '' })
  const [confirming, setConfirming] = useState(false)

  return (
    <>
      <Head title="Account" />
      <main className="mx-auto min-h-screen w-full max-w-2xl px-6 py-12">
        <header className="mb-8 flex flex-col gap-2">
          <h1 className="page-title">Account</h1>
          <p className="text-[13.5px] text-[color:var(--text-muted)]">{email}</p>
        </header>

        <div className="flex flex-col gap-6">
          <Card title="Your name" description="The person signed in, not the business">
            <form
              onSubmit={(e) => {
                e.preventDefault()
                profile.patch('/account')
              }}
              className="flex flex-col gap-5"
            >
              <Field
                label="Your name"
                htmlFor="name"
                hint="A person's name. The business name is set on the business itself, and it is what decides whether a mention counts as you."
                error={errors.name}
              >
                <Input
                  id="name"
                  name="name"
                  autoComplete="name"
                  value={profile.data.name}
                  invalid={Boolean(errors.name)}
                  onChange={(e) => profile.setData('name', e.target.value)}
                />
              </Field>
              <div>
                <Button variant="primary" as="button" loading={profile.processing}>
                  Save name
                </Button>
              </div>
            </form>
          </Card>

          <Card title="Email address" description="Confirmed before it changes">
            {!email_verified ? (
              // An unverified address is corrected at /verify, which edits it
              // outright — there is no confirmed address to protect, so it needs
              // no pending column and no password. Sending them there rather than
              // drawing a second form keeps one flow per state.
              //
              // Deliberately not the sanctioned "collection starts when you
              // confirm" string: that one is a claim about money and belongs where
              // the hold actually bites. Here the fact and the way out are what is
              // useful, and nothing is being warned about.
              <div className="flex flex-col gap-3">
                <p className="text-[13.5px] text-[color:var(--text-body)]">
                  This address is not confirmed yet.
                </p>
                <div>
                  <Button variant="secondary" href="/verify">
                    Confirm your email
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex flex-col gap-5">
                {pending_email && (
                  <Alert tone="data" icon="mail" title="Waiting on the new address">
                    A link is on its way to{' '}
                    <span className="font-mono">{pending_email}</span>. Your address stays{' '}
                    <span className="font-mono">{email}</span> until you open it, and the link lasts
                    two days.
                  </Alert>
                )}

                <form
                  onSubmit={(e) => {
                    e.preventDefault()
                    address.patch('/account/email')
                  }}
                  className="flex flex-col gap-5"
                >
                  <Field
                    label="New email address"
                    htmlFor="new_email"
                    error={errors.email}
                    hint="Your current address keeps working until you open the link we send to the new one. Password reset and every account email go there in the meantime."
                  >
                    <Input
                      id="new_email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      value={address.data.email}
                      invalid={Boolean(errors.email)}
                      onChange={(e) => address.setData('email', e.target.value)}
                    />
                  </Field>
                  <Field
                    label="Your password"
                    htmlFor="email_password"
                    required
                    // `errors.email_password`, not `errors.password`. One flat
                    // errors bag serves all four forms on this page, so a shared
                    // key renders one section's failure inside another — which is
                    // what deletion and change-password were already doing.
                    error={errors.email_password}
                    hint="Moving the address moves where a password reset goes, so it is confirmed the same way deleting the account is."
                  >
                    <Input
                      id="email_password"
                      name="password"
                      type="password"
                      autoComplete="current-password"
                      value={address.data.password}
                      invalid={Boolean(errors.email_password)}
                      onChange={(e) => address.setData('password', e.target.value)}
                    />
                  </Field>
                  <div className="flex items-center gap-3">
                    <Button
                      variant="primary"
                      as="button"
                      disabled={
                        address.data.email.trim().length === 0 ||
                        address.data.password.length === 0
                      }
                      loading={address.processing}
                    >
                      {pending_email ? 'Send the link again' : 'Send confirmation link'}
                    </Button>
                    {pending_email && (
                      // `type="button"`, which is not cosmetic: a <button> inside
                      // a <form> defaults to type="submit", so without this,
                      // cancelling would fire the send as well.
                      <Button
                        variant="ghost"
                        as="button"
                        type="button"
                        loading={cancel.processing}
                        onClick={() => cancel.delete('/account/email')}
                      >
                        Cancel this change
                      </Button>
                    )}
                  </div>
                </form>
              </div>
            )}
          </Card>

          <Card title="Change password" description="Confirmed with the password you use now">
            <form
              onSubmit={(e) => {
                e.preventDefault()
                password.patch('/account/password')
              }}
              className="flex flex-col gap-5"
            >
              <Field
                label="Current password"
                htmlFor="current_password"
                required
                error={errors.current_password}
              >
                <Input
                  id="current_password"
                  name="current_password"
                  type="password"
                  autoComplete="current-password"
                  value={password.data.current_password}
                  invalid={Boolean(errors.current_password)}
                  onChange={(e) => password.setData('current_password', e.target.value)}
                />
              </Field>
              <Field
                label="New password"
                htmlFor="new_password"
                required
                hint="At least 8 characters. You stay signed in here; anywhere else will ask for the new password."
                error={errors.password}
              >
                <Input
                  id="new_password"
                  name="password"
                  type="password"
                  autoComplete="new-password"
                  value={password.data.password}
                  invalid={Boolean(errors.password)}
                  onChange={(e) => password.setData('password', e.target.value)}
                />
              </Field>
              <Field
                label="Confirm new password"
                htmlFor="password_confirmation"
                required
                error={errors.password_confirmation}
              >
                <Input
                  id="password_confirmation"
                  name="password_confirmation"
                  type="password"
                  autoComplete="new-password"
                  value={password.data.password_confirmation}
                  invalid={Boolean(errors.password_confirmation)}
                  onChange={(e) => password.setData('password_confirmation', e.target.value)}
                />
              </Field>
              <div>
                <Button variant="primary" as="button" loading={password.processing}>
                  Change password
                </Button>
              </div>
            </form>
          </Card>

          {/*
            **The full list of this account's businesses**, each with a way to
            its Overview and to its settings.

            **"Add a business" was held back from here until 2026-10-11, and
            the reason was a dead end rather than an omission.** `BrandSetup`
            dispatches a paid collection the moment a brand is created, and the
            Overview could only show the first one — so adding a second would
            have started spending at five providers on a reading nobody could
            open. The switcher landed in the same change as this button, which
            was the condition.

            **It is offered here to everyone**, Starter and trial included,
            because this is where the list it grows is visible. The Overview
            offers it only where the plan has room — see `BusinessSwitcher` —
            and the setup page says a business counts against the plan
            separately before anything is spent.
          */}
          <Card title="Businesses" description="What is tracked on this account">
            <div className="flex flex-col gap-4">
              <ul className="flex flex-col gap-2">
                {businesses.map((business) => (
                  <li
                    key={business.id}
                    className="flex flex-wrap items-center justify-between gap-3 rounded-[9px] border border-[color:var(--border-subtle)] px-4 py-3"
                  >
                    <div className="flex min-w-0 flex-col gap-1">
                      <span className="text-[14px] text-[color:var(--text-strong)]">
                        {business.name}
                      </span>
                      <span className="text-[13px] text-[color:var(--text-muted)]">
                        {[business.industry, [business.city, business.state].filter(Boolean).join(', ')]
                          .filter(Boolean)
                          .join(' · ')}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button as={Link} href={`/dashboard?brand=${business.id}`} variant="ghost" size="sm">
                        Overview
                      </Button>
                      <Button
                        as={Link}
                        href={`/brands/${business.id}/edit`}
                        variant="ghost"
                        size="sm"
                        iconLeft="settings"
                      >
                        Settings
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
              {/* "The Overview shows the first of these" stood here until the
                  switcher made it false. What stays true is the schedule. */}
              {businesses.length > 1 && (
                <p className="max-w-[72ch] text-[13.5px] text-[color:var(--text-muted)]">
                  Each one collects on its own schedule.
                </p>
              )}
              <div>
                <Button as={Link} href="/brands/new" variant="secondary" size="sm" iconLeft="plus">
                  Add a business
                </Button>
              </div>
            </div>
          </Card>

          <Card title="Plan and usage" description="What you are paying for, and this month's allowance">
            <div className="flex flex-col gap-4">
              <p className="max-w-[72ch] text-[13.5px] text-[color:var(--text-body)]">
                Your plan, how much of this month's allowance is used, and canceling are all on
                the plan page.
              </p>
              <div>
                <Button variant="secondary" href="/billing" iconRight="arrow-right">
                  Go to plan
                </Button>
              </div>
            </div>
          </Card>

          <Divider />

          <Card title="Delete your account" description="The only action that removes anything">
            <div className="flex flex-col gap-4">
              {/*
                **Read, not held**, 2026-10-09 with item 17. The first three
                sentences were typed here verbatim and the fourth was branched on
                `grader_check_claimed` into a wording sanctioned nowhere — "You ran
                a free check before signing up, so we keep … so the limit … still
                holds", with a doubled "so". A Class 2 paraphrase on a live branch,
                reachable since `Grader::ClaimChecks` gave that prop its first
                caller on 2026-10-05, and invisible to every guard here: it is not
                withdrawn text and it is not in `marketingData.js`.

                The personalisation went with it, deliberately. One sanctioned
                string, one rendering, for every reader. `account_deletion_test`
                asserts the three promises this sentence makes as *behaviour* —
                the cascade, the global domain list, the surviving free check — so
                the link between what is said and what is checked is unaffected by
                where the words live.
              */}
              <p className="max-w-[72ch] text-[13.5px] text-[color:var(--text-body)]">
                {quoted.deletion}
              </p>
              <p className="max-w-[72ch] text-[13.5px] text-[color:var(--text-muted)]">
                {brand_count === 1
                  ? 'One business is tracked on this account.'
                  : `${brand_count} businesses are tracked on this account.`}
              </p>

              {!confirming ? (
                <div>
                  <Button variant="secondary" as="button" onClick={() => setConfirming(true)}>
                    Delete your account
                  </Button>
                </div>
              ) : (
                <form
                  onSubmit={(e) => {
                    e.preventDefault()
                    removal.delete('/account')
                  }}
                  className="flex flex-col gap-4"
                >
                  {/* `delete_password`, not `password`. Inertia gives one page one
                      flat errors bag and this page holds four forms, so `password`
                      here also rendered under Change password's "New password"
                      field — refusing a deletion put "Enter your password to delete
                      this account" on a field that has nothing to do with deleting
                      anything. */}
                  <Field
                    label="Enter your password to confirm"
                    htmlFor="delete_password"
                    error={errors.delete_password}
                  >
                    <Input
                      id="delete_password"
                      name="password"
                      type="password"
                      autoComplete="current-password"
                      value={removal.data.password}
                      invalid={Boolean(errors.delete_password)}
                      onChange={(e) => removal.setData('password', e.target.value)}
                    />
                  </Field>
                  <div className="flex items-center gap-3">
                    <Button variant="danger" as="button" loading={removal.processing}>
                      Delete this account
                    </Button>
                    {/* `type="button"`. Without it a Button inside a form defaults
                        to submit, so the control that cancels a deletion asks the
                        browser to perform one. It did not fire, because
                        setConfirming(false) unmounts the form before the click's
                        default action dispatches — which is the reason to fix it:
                        the most destructive action in the product was safe by
                        React's event-flush timing rather than by anything stated. */}
                    <Button variant="ghost" as="button" type="button" onClick={() => setConfirming(false)}>
                      Keep it
                    </Button>
                  </div>
                </form>
              )}
            </div>
          </Card>
        </div>
      </main>
    </>
  )
}

AccountEdit.layout = withSignedInLayout
